import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo, faSearch, faFileInvoice, faCalendarAlt, faUser, faPhone,
    faMapMarkerAlt, faBox, faTag, faBarcode, faRupeeSign, faBalanceScale,
    faDollarSign, faCalculator, faExclamationTriangle, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faCreditCard,
    faMoneyBill, faWallet, faUniversity, faPercent, faReceipt, faChevronDown,
    faChevronUp, faBoxes, faHistory, faShoppingCart, faStore, faPlus,
    faMinus, faFilter, faPrint, faFilePdf, faQrcode, faTimesCircle,
    faIdCard, faLock, faEye, faEyeSlash, faCopy, faList, faFileAlt, faTrashAlt,
    faToggleOn, faToggleOff
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API Endpoints - Updated to pharmacy_module
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/pharmacy_module';
const RETURN_API = `${API_BASE}/pharmacy_return.php`;

// ==============================================
// Helper function to parse BQuantity
// ==============================================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) {
        return parseFloat(match[1].replace(/,/g, ''));
    }
    return 0;
};

// ==============================================
// Translation Utility - Same as sell.js
// ==============================================
const createReturnTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_return_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Return Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_return_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Process Return': 'واپسی پر کارروائی کریں',
                'Return Items': 'آئٹمز واپس کریں',
                'Return Information': 'واپسی کی معلومات',
                'Return Number': 'واپسی نمبر',
                'Return Date': 'واپسی تاریخ',
                'Original Invoice': 'اصل انوائس',
                'Original Bill Number': 'اصل بل نمبر',
                'Original Date': 'اصل تاریخ',
                'Customer Details': 'کسٹمر کی تفصیلات',
                'Customer Name': 'کسٹمر کا نام',
                'Phone Number': 'فون نمبر',
                'Address': 'پتہ',
                'Search Invoice': 'انوائس تلاش کریں',
                'Enter Invoice': 'انوائس نمبر درج کریں',
                'Invoice found': 'انوائس مل گئی',
                'Invoice not found': 'انوائس نہیں ملی',
                'Items to Return': 'واپس کرنے کے لیے آئٹمز',
                'Product': 'پروڈکٹ',
                'Product Code': 'پروڈکٹ کوڈ',
                'Company': 'کمپنی',
                'Batch': 'بیچ',
                'Unit': 'یونٹ',
                'Price': 'قیمت',
                'Qty to Return': 'واپسی کی مقدار',
                'Available': 'دستیاب',
                'Total': 'کل',
                'Remove': 'ہٹائیں',
                'Return Calculations': 'واپسی کے حسابات',
                'Subtotal': 'ذیلی کل',
                'Tax': 'ٹیکس',
                'Discount': 'ڈسکاؤنٹ',
                'Grand Total': 'کل رقم',
                'Refund Amount': 'واپسی رقم',
                'Refund Method': 'واپسی کا طریقہ',
                'Cash': 'نقد',
                'Card': 'کارڈ',
                'UPI': 'یوپی آئی',
                'Bank Transfer': 'بینک ٹرانسفر',
                'Wallet': 'والٹ',
                'Return Reason': 'واپسی کی وجہ',
                'Notes': 'نوٹس',
                'Process Return': 'واپسی پر کارروائی کریں',
                'Clear': 'صاف کریں',
                'Processing...': 'کارروائی جاری ہے...',
                'Loading...': 'لوڈ ہو رہا ہے...',
                'Return processed successfully!': 'واپسی کامیابی سے مکمل ہو گئی!',
                'Failed to process return': 'واپسی پر کارروائی نہیں ہو سکی',
                'Network error occurred': 'نیٹ ورک کی خرابی',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'At least one item is required for return': 'کم از کم ایک آئٹم واپسی کے لیے ضروری ہے',
                'Quantity exceeds available stock': 'مقدار دستیاب اسٹاک سے زیادہ ہے',
                'Please enter a valid quantity': 'براہ کرم درست مقدار درج کریں',
                'Return reason is required': 'واپسی کی وجہ ضروری ہے',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Save Draft': 'ڈرافٹ محفوظ کریں',
                'Drafts': 'ڈرافٹس',
                'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                'Clear Form': 'فارم صاف کریں',
                'View Drafts': 'ڈرافٹس دیکھیں',
                'Go Back': 'واپس جائیں',
                'Search Invoice Number': 'انوائس نمبر تلاش کریں',
                'Search': 'تلاش کریں',
                'Return Receipt': 'واپسی رسید',
                'Return #': 'واپسی #',
                'Original Invoice #': 'اصل انوائس #',
                'Phone': 'فون',
                'Refund': 'واپسی',
                'Thank you for your business!': 'آپ کے کاروبار کا شکریہ!',
                'Powered by OurZest': 'OurZest کی طرف سے تقویت یافتہ'
            },
            'ar': {
                'Process Return': 'معالجة الإرجاع',
                'Return Items': 'إرجاع العناصر',
                'Return Information': 'معلومات الإرجاع',
                'Return Number': 'رقم الإرجاع',
                'Return Date': 'تاريخ الإرجاع',
                'Original Invoice': 'الفاتورة الأصلية',
                'Original Bill Number': 'رقم الفاتورة الأصلي',
                'Original Date': 'التاريخ الأصلي',
                'Customer Details': 'تفاصيل العميل',
                'Customer Name': 'اسم العميل',
                'Phone Number': 'رقم الهاتف',
                'Address': 'العنوان',
                'Search Invoice': 'البحث عن فاتورة',
                'Enter Invoice': 'أدخل رقم الفاتورة',
                'Invoice found': 'تم العثور على الفاتورة',
                'Invoice not found': 'لم يتم العثور على الفاتورة',
                'Items to Return': 'العناصر المراد إرجاعها',
                'Product': 'المنتج',
                'Product Code': 'رمز المنتج',
                'Company': 'الشركة',
                'Batch': 'الدفعة',
                'Unit': 'الوحدة',
                'Price': 'السعر',
                'Qty to Return': 'كمية الإرجاع',
                'Available': 'متوفر',
                'Total': 'المجموع',
                'Remove': 'إزالة',
                'Return Calculations': 'حسابات الإرجاع',
                'Subtotal': 'المجموع الفرعي',
                'Tax': 'الضريبة',
                'Discount': 'الخصم',
                'Grand Total': 'المجموع الكلي',
                'Refund Amount': 'مبلغ الاسترداد',
                'Refund Method': 'طريقة الاسترداد',
                'Cash': 'نقدي',
                'Card': 'بطاقة',
                'UPI': 'UPI',
                'Bank Transfer': 'تحويل بنكي',
                'Wallet': 'محفظة',
                'Return Reason': 'سبب الإرجاع',
                'Notes': 'ملاحظات',
                'Process Return': 'معالجة الإرجاع',
                'Clear': 'مسح',
                'Processing...': 'جاري المعالجة...',
                'Loading...': 'جاري التحميل...',
                'Return processed successfully!': 'تمت معالجة الإرجاع بنجاح!',
                'Failed to process return': 'فشل في معالجة الإرجاع',
                'Network error occurred': 'حدث خطأ في الشبكة',
                'No shop selected': 'لم يتم اختيار متجر',
                'At least one item is required for return': 'مطلوب عنصر واحد على الأقل للإرجاع',
                'Quantity exceeds available stock': 'الكمية تتجاوز المخزون المتوفر',
                'Please enter a valid quantity': 'الرجاء إدخال كمية صحيحة',
                'Return reason is required': 'سبب الإرجاع مطلوب',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Save Draft': 'حفظ المسودة',
                'Drafts': 'المسودات',
                'Saved Drafts': 'المسودات المحفوظة',
                'No drafts saved': 'لا توجد مسودات محفوظة',
                'Draft loaded': 'تم تحميل المسودة',
                'Draft cleared': 'تم مسح المسودة',
                'Clear Form': 'مسح النموذج',
                'View Drafts': 'عرض المسودات',
                'Go Back': 'رجوع',
                'Search Invoice Number': 'البحث عن رقم الفاتورة',
                'Search': 'بحث',
                'Return Receipt': 'إيصال الإرجاع',
                'Return #': 'إرجاع #',
                'Original Invoice #': 'الفاتورة الأصلية #',
                'Phone': 'هاتف',
                'Refund': 'استرداد',
                'Thank you for your business!': 'شكرا لتعاملكم معنا!',
                'Powered by OurZest': 'مدعوم من OurZest'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        processReturn: 'Process Return',
        returnItems: 'Return Items',
        returnInformation: 'Return Information',
        returnNumber: 'Return Number',
        returnDate: 'Return Date',
        originalInvoice: 'Original Invoice',
        originalBillNumber: 'Original Bill Number',
        originalDate: 'Original Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchInvoice: 'Search Invoice',
        searchPlaceholder: 'Enter Invoice Number, Customer Name or Phone',
        invoiceFound: 'Invoice found',
        invoiceNotFound: 'Invoice not found',
        itemsToReturn: 'Items to Return',
        productName: 'Product',
        productCode: 'Product Code',
        company: 'Company',
        batchNumber: 'Batch',
        unit: 'Unit',
        price: 'Price',
        quantityToReturn: 'Qty to Return',
        available: 'Sold',
        total: 'Total',
        remove: 'Remove',
        returnCalculations: 'Return Calculations',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        refundAmount: 'Refund Amount',
        refundMethod: 'Refund Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        wallet: 'Wallet',
        returnReason: 'Return Reason',
        notes: 'Notes',
        processReturnBtn: 'Process Return',
        clear: 'Clear',
        saving: 'Processing...',
        loading: 'Loading...',
        returnProcessed: 'Return processed successfully!',
        failedProcess: 'Failed to process return',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected',
        itemsRequired: 'At least one item is required for return',
        quantityExceedsAvailable: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        returnReasonRequired: 'Return reason is required',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        search: 'Search',
        returnReceipt: 'Return Receipt',
        returnHash: 'Return #',
        originalInvoiceHash: 'Original Invoice #',
        phone: 'Phone',
        refund: 'Refund',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const returnTranslator = createReturnTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// IOS Style Toggle Switch Component
// ==============================================
const IOSToggleSwitch = ({ checked, onChange, disabled, label, activeLabel, inactiveLabel }) => {
    return (
        <button
            type="button"
            className={`ozui-ios-toggle ${checked ? 'ozui-checked' : ''}`}
            onClick={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
        >
            <span className="ozui-ios-slider" />
            <span className="ozui-ios-label">
                {checked ? (activeLabel || label || 'Pack') : (inactiveLabel || label || 'Unit')}
            </span>
        </button>
    );
};

// ==============================================
// Return Items Table Component
// ==============================================
const ReturnItemsTable = ({
    items,
    onQuantityChange,
    onRemove,
    onToggleSellMethod,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    if (isMobile) {
        return (
            <div className="ozui-items-mobile-container">
                <div className="ozui-items-header-mobile">
                    <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsToReturn || 'Items to Return'}</h4>
                </div>
                <div className="ozui-items-cards-container">
                    {items.map((item, index) => {
                        const isPackingMode = item.sellMethod === 'packing';
                        return (
                            <div key={index} className="ozui-item-card-mobile">
                                <div className="ozui-item-card-header">
                                    <span className="ozui-item-number">{item.product_name}</span>
                                    <div className="ozui-sell-method-toggle-mobile">
                                        <span className={`ozui-method-badge ${isPackingMode ? 'ozui-packing' : 'ozui-subpacking'}`}>
                                            {isPackingMode ? (item.packing_type || 'Pack') : (item.sub_packing_type || 'Unit')}
                                        </span>
                                        {onToggleSellMethod && (
                                            <button
                                                type="button"
                                                className="ozui-toggle-method-btn"
                                                onClick={() => onToggleSellMethod(index)}
                                                disabled={disabled}
                                            >
                                                <FontAwesomeIcon icon={isPackingMode ? faToggleOn : faToggleOff} />
                                            </button>
                                        )}
                                    </div>
                                    <button type="button" className="ozui-item-remove-btn-mobile" onClick={() => onRemove(index)} disabled={disabled}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="ozui-item-card-content">
                                    {item.product_code && (
                                        <div className="ozui-mobile-field">
                                            <label>{translations?.productCode || 'Product Code'}</label>
                                            <input type="text" value={item.product_code} readOnly className="ozui-readonly-input" />
                                        </div>
                                    )}
                                    <div className="ozui-mobile-field">
                                        <label>{translations?.batchNumber || 'Batch'}</label>
                                        <input type="text" value={item.batch_no || '-'} readOnly className="ozui-readonly-input" />
                                    </div>
                                    <div className="ozui-mobile-field">
                                        <label>{translations?.unit || 'Unit'}</label>
                                        <input type="text" value={item.unit} readOnly className="ozui-readonly-input" />
                                    </div>
                                    <div className="ozui-mobile-row">
                                        <div className={`ozui-mobile-field half ${errors[`quantity_${index}`] ? 'ozui-has-error' : ''}`}>
                                            <label>{translations?.quantityToReturn || 'Qty to Return'} *</label>
                                            <input 
                                                type="number" 
                                                value={item.return_quantity || ''} 
                                                onChange={(e) => onQuantityChange(index, e.target.value)} 
                                                step="0.001" 
                                                min="0.001" 
                                                max={item.available_for_return} 
                                                placeholder="0.000" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                            />
                                            <small className="ozui-stock-hint">
                                                {translations?.available || 'Available'}: {parseFloat(item.available_for_return || 0).toFixed(2)} {item.unit}
                                            </small>
                                        </div>
                                        <div className="ozui-mobile-field half">
                                            <label>{translations?.price || 'Price'}</label>
                                            <input type="text" value={item.price} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </div>
                                    <div className="ozui-mobile-row">
                                        <div className="ozui-mobile-field half">
                                            <label>{translations?.total || 'Total'}</label>
                                            <input type="text" value={calculateTotal(item.price, item.return_quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                        </div>
                                    </div>
                                    {errors[`quantity_${index}`] && (
                                        <div className="ozui-error-text"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // DESKTOP VIEW
    return (
        <div className="ozui-items-table-container">
            <div className="ozui-table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.itemsToReturn || 'Items to Return'}</h4>
            </div>
            <div className="ozui-table-wrapper">
                <table className="ozui-items-table">
                    <thead>
                        <tr>
                            <th>{translations?.productName || 'Product'}</th>
                            <th>{translations?.productCode || 'Product Code'}</th>
                            <th>{translations?.batchNumber || 'Batch'}</th>
                            <th>{translations?.unit || 'Unit'}</th>
                            <th>{translations?.price || 'Price'}</th>
                            <th>{translations?.quantityToReturn || 'Qty to Return'}</th>
                            <th>{translations?.total || 'Total'}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            return (
                                <tr key={index} className={errors[`quantity_${index}`] ? 'ozui-error-row' : ''}>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.product_name} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.product_code || '-'} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.batch_no || '-'} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.unit} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="number" value={item.price} readOnly className="ozui-readonly-input" />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="number" 
                                                value={item.return_quantity || ''} 
                                                onChange={(e) => onQuantityChange(index, e.target.value)} 
                                                step="0.001" 
                                                min="0.001" 
                                                max={item.available_for_return} 
                                                placeholder="0.000" 
                                                disabled={disabled} 
                                                dir="ltr" 
                                                className={errors[`quantity_${index}`] ? 'ozui-error' : ''} 
                                            />
                                            <small className="ozui-stock-hint">
                                                {translations?.available || 'Available'}: {parseFloat(item.available_for_return || 0).toFixed(2)} {item.unit}
                                            </small>
                                            {errors[`quantity_${index}`] && <div className="ozui-table-error"><FontAwesomeIcon icon={faExclamationTriangle} />{errors[`quantity_${index}`]}</div>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={calculateTotal(item.price, item.return_quantity)} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                        </div>
                                    </td>
                                    <td>
                                        <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled} title={translations?.remove || 'Remove'}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// MAIN RETURN COMPONENT
// ==============================================
const PharmacyReturn = () => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        return_number: '',
        return_number_int: 0,
        sell_id: null,
        date: new Date().toISOString().split('T')[0],
        customer_id: null,
        customer_name: '',
        customer_phone: '',
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        refund_amount: '0.00',
        payment_method: 'cash',
        return_reason: '',
        notes: '',
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [searchTerm, setSearchTerm] = useState('');
    const [searching, setSearching] = useState(false);
    const [invoiceFound, setInvoiceFound] = useState(false);
    const [originalSale, setOriginalSale] = useState(null);
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        bussinesslogo_url: '',
        businessaddress: '',
        fullname: '',
        email: '',
        phonenumber: '',
        gst_number: ''
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [hasDraft, setHasDraft] = useState(false);
    
    // Draft State
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    
    // Auto-focus ref for Invoice Search field
    const invoiceSearchRef = useRef(null);
    
    // Refs
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const isClearingRef = useRef(false);
    const isInitialLoadRef = useRef(true);
    const draftsDropdownRef = useRef(null);
    const submitButtonRef = useRef(null);

    // Tab key handler to close all dropdowns
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus on Invoice Search field when component mounts
    useEffect(() => {
        setTimeout(() => {
            if (invoiceSearchRef.current) {
                invoiceSearchRef.current.focus();
            }
        }, 100);
    }, []);

    // ============================================
    // Draft Management Functions
    // ============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `return_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.sell_id || (formData.items && formData.items.some(item => parseFloat(item.return_quantity) > 0)) || formData.return_reason?.trim();
        if (!hasData) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            searchTerm,
            originalSale,
            invoiceFound,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(limitedDrafts));
        
        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            ...initialFormData,
            ...draft,
            date: draft.date || new Date().toISOString().split('T')[0],
            items: draft.items || []
        });
        if (draft.searchTerm) setSearchTerm(draft.searchTerm);
        if (draft.originalSale) setOriginalSale(draft.originalSale);
        if (draft.invoiceFound) setInvoiceFound(draft.invoiceFound);
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
        
        setTimeout(() => {
            if (invoiceSearchRef.current) {
                invoiceSearchRef.current.focus();
            }
        }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearDraft = () => {
        if (isSubmittingRef.current) { showToast('warning', 'Please wait for current operation to complete'); return; }
        isClearingRef.current = true;
        clearDraftFromStorage();
        setFormData(initialFormData);
        setSearchTerm('');
        setInvoiceFound(false);
        setOriginalSale(null);
        setErrors({});
        setHasDraft(false);
        getNextReturnNumber();
        showToast('success', translations.draftCleared || 'Form cleared');
        
        setTimeout(() => {
            if (invoiceSearchRef.current) {
                invoiceSearchRef.current.focus();
            }
        }, 100);
        
        setTimeout(() => { isClearingRef.current = false; }, 500);
    };

    const clearDraftFromStorage = () => {
        try { 
            localStorage.removeItem(getDraftStorageKey()); 
            setHasDraft(false); 
        } catch (error) {}
    };
    
    const checkDraftExists = () => {
        try { 
            setHasDraft(!!localStorage.getItem(getDraftStorageKey())); 
        } catch (error) {}
    };

    // ============================================
    // Theme Color Auto-Sync
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('dynamic-orb-styles-return');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-return';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // ============================================
    // Dark Mode Auto-Sync
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ============================================
    // Language Auto-Sync
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await returnTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL Direction Sync
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Load drafts on shop change
    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // Position drafts dropdown
    useEffect(() => {
        if (showDraftsList && draftsDropdownRef.current) {
            const button = draftsDropdownRef.current.querySelector('.drafts-btn');
            const dropdown = draftsDropdownRef.current.querySelector('.ozui-drafts-dropdown');
            
            if (button && dropdown) {
                const rect = button.getBoundingClientRect();
                dropdown.style.position = 'fixed';
                dropdown.style.top = `${rect.bottom + 8}px`;
                dropdown.style.right = `${window.innerWidth - rect.right}px`;
            }
        }
    }, [showDraftsList]);

    // Click outside for drafts dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    useEffect(() => { checkDraftExists(); }, [currentShop?.shop_id]);

    useEffect(() => {
        if (currentShop?.shop_id && isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            const savedDraft = localStorage.getItem(getDraftStorageKey());
            if (savedDraft) {
                try {
                    const draftData = JSON.parse(savedDraft);
                    setFormData(prev => ({ ...prev, ...draftData.formData }));
                    if (draftData.searchTerm) setSearchTerm(draftData.searchTerm);
                    if (draftData.originalSale) setOriginalSale(draftData.originalSale);
                    if (draftData.invoiceFound) setInvoiceFound(draftData.invoiceFound);
                    setHasDraft(true);
                    showToast('info', translations?.draftLoaded || 'Loaded saved draft');
                } catch (error) {}
            }
        }
    }, [currentShop?.shop_id, translations]);

    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    bussinesslogo_url: data.bussinesslogo_url || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || '',
                    phonenumber: data.phonenumber || '',
                    gst_number: data.gst_number || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: user.bussinesslogo || '',
                    bussinesslogo_url: user.bussinesslogo_url || '',
                    businessaddress: user.business_address || '',
                    fullname: user.fullname || '',
                    email: user.email || '',
                    phonenumber: user.phonenumber || '',
                    gst_number: user.gst_number || ''
                });
            }
        } catch (error) {
            console.error('Error loading business info:', error);
            setBusinessInfo({
                businessname: user.business_name || user.fullname || '',
                bussinesslogo: user.bussinesslogo || '',
                bussinesslogo_url: user.bussinesslogo_url || '',
                businessaddress: user.business_address || '',
                fullname: user.fullname || '',
                email: user.email || '',
                phonenumber: user.phonenumber || '',
                gst_number: user.gst_number || ''
            });
        }
    }, [user]);

    useEffect(() => {
        if (currentShop?.shop_id) {
            getNextReturnNumber();
        }
        loadBusinessInfo();
    }, [currentShop, loadBusinessInfo]);

    const getNextReturnNumber = async () => {
        if (!currentShop?.shop_id) return;
        try {
            const response = await fetch(RETURN_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_next_return_number', shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, return_number: data.data.return_number, return_number_int: data.data.return_number_int }));
            }
        } catch (error) {
            console.error('Error getting next return number:', error);
        }
    };

    const searchInvoice = async () => {
        if (!searchTerm.trim()) {
            showToast('error', translations?.searchPlaceholder || 'Please enter an invoice number');
            return;
        }
        if (!currentShop?.shop_id) {
            showToast('error', translations?.noShopSelected || 'No shop selected');
            return;
        }
        setSearching(true);
        setInvoiceFound(false);
        setOriginalSale(null);
        setFormData(prev => ({
            ...prev, sell_id: null, customer_id: null, customer_name: '', customer_phone: '',
            subtotal: '0.00', tax: '0.00', discount: '0.00', grand_total: '0.00', refund_amount: '0.00', items: []
        }));
        try {
            const response = await fetch(RETURN_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_invoice', search_term: searchTerm, shop_id: currentShop.shop_id })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                setInvoiceFound(true);
                setOriginalSale(data.data);
                const returnItems = data.data.items.map(item => ({
                    ...item,
                    return_quantity: '',
                    sell_item_id: item.id,
                    product_code: item.product_code || '',
                    company_name: item.company_name || item.company || '',
                    available_for_return: parseFloat(item.available_for_return) || 0,
                    sellMethod: item.sell_method || 'sub_packing',
                    packing_type: item.packing_type || null,
                    sub_packing_type: item.sub_packing_type || null,
                    items_per_pack: parseFloat(item.items_per_pack) || 1,
                    unit_of_measure: item.unit_of_measure || 'piece'
                }));
                setFormData(prev => ({
                    ...prev,
                    sell_id: data.data.id,
                    customer_id: data.data.customer_id,
                    customer_name: data.data.customer_name || '',
                    customer_phone: data.data.customer_phone || '',
                    subtotal: '0.00',
                    grand_total: '0.00',
                    refund_amount: '0.00',
                    items: returnItems
                }));
                showToast('success', `${translations?.invoiceFound || 'Invoice found'}: #${data.data.bill_number}`);
            } else {
                showToast('error', data.message || translations?.invoiceNotFound || 'Invoice not found');
            }
        } catch (error) {
            console.error('Error searching invoice:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setSearching(false);
        }
    };

    const toggleSellMethod = (index) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const currentMethod = item.sellMethod;
        const newMethod = currentMethod === 'packing' ? 'sub_packing' : 'packing';
        
        const currentQty = parseFloat(item.return_quantity) || 0;
        let convertedQty = currentQty;
        
        if (newMethod === 'packing') {
            convertedQty = currentQty / (item.items_per_pack || 1);
        } else {
            convertedQty = currentQty * (item.items_per_pack || 1);
        }
        
        convertedQty = parseFloat(convertedQty.toFixed(3));
        
        if (convertedQty > item.available_for_return) {
            showToast('warning', `Not enough stock for ${newMethod === 'packing' ? 'pack' : 'unit'} mode. Quantity adjusted.`);
            convertedQty = item.available_for_return;
        }
        
        updatedItems[index] = {
            ...item,
            sellMethod: newMethod,
            return_quantity: convertedQty > 0 ? convertedQty : ''
        };
        
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleQuantityChange = (index, value) => {
        const updatedItems = [...formData.items];
        const item = updatedItems[index];
        const qty = value === '' ? '' : parseFloat(value);
        if (qty !== '' && (isNaN(qty) || qty < 0)) return;
        if (qty !== '' && qty > item.available_for_return) {
            setErrors(prev => ({ ...prev, [`quantity_${index}`]: `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${item.available_for_return}` }));
            return;
        }
        if (errors[`quantity_${index}`]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
        }
        item.return_quantity = qty === '' ? '' : qty;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const removeItem = (index) => {
        const updatedItems = [...formData.items];
        updatedItems.splice(index, 1);
        setFormData(prev => ({ ...prev, items: updatedItems }));
        setErrors(prev => { const newErrors = { ...prev }; delete newErrors[`quantity_${index}`]; return newErrors; });
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let hasAnyReturn = false;
        formData.items.forEach(item => {
            if (item.return_quantity && parseFloat(item.return_quantity) > 0) {
                hasAnyReturn = true;
                const price = parseFloat(item.price) || 0;
                const quantity = parseFloat(item.return_quantity) || 0;
                subtotal += price * quantity;
            }
        });
        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const refundAmount = grandTotal;
        setFormData(prev => ({ ...prev, subtotal: subtotal.toFixed(2), grand_total: grandTotal.toFixed(2), refund_amount: refundAmount.toFixed(2) }));
        return hasAnyReturn;
    };

    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.sell_id) newErrors.invoice = translations?.invoiceNotFound || 'Please search and select an invoice first';
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required for return';
        } else {
            let hasValidReturn = false;
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.return_quantity) || 0;
                if (qty > 0) {
                    hasValidReturn = true;
                    if (qty > item.available_for_return) newErrors[`quantity_${index}`] = `${translations?.quantityExceedsAvailable || 'Cannot return more than'} ${item.available_for_return}`;
                    if (qty <= 0) newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                }
            });
            if (!hasValidReturn) newErrors.items = translations?.itemsRequired || 'Please enter at least one item to return';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // ============================================
    // PRINT RECEIPT FUNCTION - Thermal Printer Only
    // ============================================
    const handlePrintReceipt = (returnData) => {
        if (!returnData || !returnData.items || returnData.items.length === 0) {
            showToast('warning', 'No items to print return receipt');
            return;
        }

        const printWindow = window.open('', '_blank');

        if (printWindow) {
            const shopName = businessInfo?.businessname || user?.fullname || currentShop?.shop_name || 'OurZest Store';
            const shopAddress = businessInfo?.businessaddress || '';
            const shopPhone = businessInfo?.phonenumber || '';
            const businessEmail = businessInfo?.email || '';
            const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
            const customerName = returnData?.customer_name || 'Walk-in Customer';
            const customerPhone = returnData?.customer_phone || '';
            const themeColor = currentThemeColor || '#6366f1';

            const formatCurrency = (amount) => {
                if (!amount && amount !== 0) return 'Rs. 0.00';
                return `Rs. ${parseFloat(amount).toFixed(2)}`;
            };

            const formatDate = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
            };

            const escapeHtml = (text) => {
                if (!text) return '';
                return String(text).replace(/[&<>]/g, function(m) {
                    if (m === '&') return '&amp;';
                    if (m === '<') return '&lt;';
                    if (m === '>') return '&gt;';
                    return m;
                });
            };

            const getUnitLabel = (item) => {
                if (item.sell_method === 'packing') {
                    return item.packing_type || 'Box';
                } else if (item.sell_method === 'base_unit') {
                    return item.pack_base_unit_label || 'Unit';
                } else {
                    return item.unitorstrip || item.sub_packing_type || 'Unit';
                }
            };

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Return Receipt ${returnData.return_number}</title>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <style>
                        /* 3-inch Thermal Printer Styles - 80mm width */
                        @page { 
                            size: 80mm auto; 
                            margin: 0; 
                        }
                        * { 
                            margin: 0; 
                            padding: 0; 
                            box-sizing: border-box; 
                        }
                        body { 
                            font-family: 'Arial', 'Helvetica Neue', sans-serif; 
                            background: #ffffff; 
                            color: #1a1a2e; 
                            font-size: 9pt; 
                            line-height: 1.4;
                            padding: 3mm 3.5mm;
                            width: 80mm;
                            margin: 0 auto;
                        }
                        .receipt {
                            max-width: 73mm;
                            margin: 0 auto;
                        }
                        .text-center { text-align: center; }
                        .text-right { text-align: right; }
                        .text-left { text-align: left; }
                        .text-uppercase { text-transform: uppercase; }
                        .font-bold { font-weight: 700; }
                        .font-small { font-size: 7.5pt; }
                        .font-large { font-size: 13pt; }
                        .font-xlarge { font-size: 15pt; }
                        
                        /* ===== HEADER ===== */
                        .header {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 12px;
                            padding: 4px 0 6px 0;
                            border-bottom: 2.5px solid #1a1a2e;
                            margin-bottom: 5px;
                        }
                        .header-logo-wrapper {
                            flex-shrink: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                        .header-logo {
                            max-height: 70px;
                            max-width: 70px;
                            height: auto;
                            width: auto;
                            display: block;
                            object-fit: contain;
                        }
                        .header-info {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            line-height: 1.2;
                        }
                        .shop-name {
                            font-size: 15pt;
                            font-weight: 800;
                            letter-spacing: 0.8px;
                            color: #1a1a2e;
                        }
                        .shop-address {
                            font-size: 7.5pt;
                            color: #4a4a5a;
                            font-weight: 400;
                        }
                        .shop-contact {
                            font-size: 7.5pt;
                            color: #4a4a5a;
                            font-weight: 400;
                        }
                        .shop-contact .phone {
                            font-weight: 600;
                            color: #1a1a2e;
                        }
                        
                        /* ===== DIVIDERS ===== */
                        .divider {
                            border-top: 1px dashed #c0c0d0;
                            margin: 3px 0;
                        }
                        .divider-thick {
                            border-top: 2px solid #1a1a2e;
                            margin: 3px 0;
                        }
                        .divider-dotted {
                            border-top: 1px dotted #d0d0e0;
                            margin: 2px 0;
                        }
                        .divider-light {
                            border-top: 1px solid #e8e8f0;
                            margin: 2px 0;
                        }
                        
                        /* ===== BILL INFO ===== */
                        .bill-info {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            padding: 2px 0;
                            border-bottom: 1px solid #e8e8f0;
                            margin-bottom: 3px;
                        }
                        .bill-number {
                            font-size: 10pt;
                            font-weight: 700;
                            color: #1a1a2e;
                            letter-spacing: 0.5px;
                        }
                        .bill-number .label {
                            font-weight: 600;
                            color: #4a4a5a;
                        }
                        .bill-meta {
                            display: flex;
                            justify-content: center;
                            gap: 20px;
                            font-size: 8.5pt;
                            color: #4a4a5a;
                            padding: 1px 0;
                        }
                        .bill-meta .label {
                            font-weight: 600;
                            color: #1a1a2e;
                        }
                        
                        /* ===== CUSTOMER INFO ===== */
                        .customer-info {
                            text-align: center;
                            font-size: 8.5pt;
                            padding: 3px 0;
                            border-bottom: 1px solid #e8e8f0;
                            margin-bottom: 4px;
                            color: #2a2a3e;
                        }
                        .customer-info .label {
                            font-weight: 600;
                            color: #1a1a2e;
                        }
                        
                        /* ===== ITEMS TABLE ===== */
                        .items-table {
                            width: 100%;
                            border-collapse: collapse;
                            font-size: 8.5pt;
                            margin: 3px 0;
                        }
                        .items-table th {
                            text-align: left;
                            padding: 3px 1px;
                            border-bottom: 1.5px solid #1a1a2e;
                            font-size: 7.5pt;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            color: #1a1a2e;
                        }
                        .items-table td {
                            padding: 2.5px 1px;
                            vertical-align: middle;
                            border-bottom: 1px solid #f0f0f5;
                        }
                        .items-table tr:last-child td {
                            border-bottom: none;
                        }
                        .items-table .col-item {
                            width: 40%;
                        }
                        .items-table .col-qty {
                            text-align: center;
                            width: 20%;
                        }
                        .items-table .col-price {
                            text-align: right;
                            width: 18%;
                        }
                        .items-table .col-total {
                            text-align: right;
                            width: 22%;
                        }
                        .items-table .item-name {
                            font-weight: 500;
                            font-size: 8.5pt;
                            color: #1a1a2e;
                            word-break: break-word;
                        }
                        .items-table .item-unit {
                            font-size: 7pt;
                            color: #6a6a7a;
                            font-weight: 400;
                            display: inline;
                        }
                        .items-table .qty-with-unit {
                            display: inline;
                            font-weight: 500;
                        }
                        .items-table .qty-with-unit .unit {
                            font-weight: 400;
                            color: #6a6a7a;
                            margin-left: 1px;
                        }
                        
                        /* ===== TOTALS ===== */
                        .totals {
                            margin-top: 3px;
                            padding-top: 4px;
                            border-top: 2px solid #1a1a2e;
                        }
                        .total-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 1.5px 0;
                            font-size: 8.5pt;
                        }
                        .total-row .label {
                            font-weight: 600;
                            color: #1a1a2e;
                        }
                        .total-row .value {
                            font-weight: 500;
                            color: #1a1a2e;
                        }
                        .total-row.grand-total {
                            font-size: 11.5pt;
                            font-weight: 700;
                            border-top: 2px solid #1a1a2e;
                            padding-top: 4px;
                            margin-top: 2px;
                        }
                        .total-row.grand-total .label {
                            font-size: 11.5pt;
                        }
                        .total-row.grand-total .value {
                            font-size: 11.5pt;
                        }
                        .total-row .value.text-success { color: #059669; }
                        .total-row .value.text-danger { color: #dc2626; }
                        .total-row .value.text-warning { color: #d97706; }
                        .total-row .value.text-primary { color: #2563eb; }
                        .total-row .value.text-refund { color: #dc2626; font-weight: 700; }
                        
                        /* ===== RETURN STATUS ===== */
                        .return-status {
                            text-align: center;
                            padding: 3px 0;
                            margin: 3px 0;
                            font-size: 9.5pt;
                            font-weight: 700;
                            letter-spacing: 1.5px;
                            border-radius: 2px;
                            color: #059669;
                            background: #ecfdf5;
                        }
                        
                        /* ===== PAYMENT METHOD ===== */
                        .payment-method {
                            display: flex;
                            justify-content: space-between;
                            font-size: 8pt;
                            padding: 2px 0;
                            color: #1a1a2e;
                        }
                        .payment-method .label {
                            font-weight: 600;
                        }
                        .payment-method .value {
                            font-weight: 500;
                            text-transform: uppercase;
                        }
                        
                        /* ===== REASON BOX ===== */
                        .reason-box {
                            background: #fef3c7;
                            padding: 4px 8px;
                            border-radius: 2px;
                            font-size: 7.5pt;
                            color: #92400e;
                            margin-top: 4px;
                            border-left: 3px solid #d97706;
                            word-break: break-word;
                            text-align: center;
                        }
                        
                        /* ===== FOOTER ===== */
                        .footer {
                            text-align: center;
                            padding-top: 5px;
                            border-top: 2px solid #1a1a2e;
                            margin-top: 5px;
                        }
                        .footer .thankyou {
                            font-size: 9pt;
                            font-weight: 700;
                            color: #1a1a2e;
                            letter-spacing: 1px;
                        }
                        .footer .powered {
                            font-size: 6.5pt;
                            color: #9a9aaa;
                            margin-top: 2px;
                            letter-spacing: 0.5px;
                        }
                        
                        /* ===== PRINT OPTIMIZATIONS ===== */
                        @media print {
                            body { padding: 2mm 2.5mm; }
                            .no-print { display: none; }
                        }
                        
                        @media print and (max-width: 80mm) {
                            body { padding: 1.5mm 2mm; }
                            .receipt { max-width: 100%; }
                            .shop-name { font-size: 13pt; }
                            .header-logo { max-height: 60px; max-width: 60px; }
                            .header { gap: 10px; padding: 3px 0 5px 0; }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt">
                        <!-- ===== HEADER ===== -->
                        <div class="header">
                            ${businessLogo ? `
                            <div class="header-logo-wrapper">
                                <img src="${businessLogo}" alt="${escapeHtml(shopName)}" class="header-logo" onerror="this.style.display='none'" />
                            </div>
                            ` : ''}
                            <div class="header-info">
                                <div class="shop-name">${escapeHtml(shopName)}</div>
                                ${shopAddress ? `<div class="shop-address">${escapeHtml(shopAddress)}</div>` : ''}
                                ${shopPhone ? `<div class="shop-contact">📞 <span class="phone">${escapeHtml(shopPhone)}</span></div>` : ''}
                            </div>
                        </div>

                        <!-- ===== RETURN INFO ===== -->
                        <div class="bill-info">
                            <div class="bill-number">
                                <span class="label">RETURN #</span> ${returnData.return_number}
                            </div>
                            <div class="bill-meta">
                                <span><span class="label">Date:</span> ${formatDate(returnData.date)}</span>
                                <span><span class="label">Time:</span> ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div style="font-size:8pt; color:#4a4a5a; padding:1px 0;">
                                <span class="label">Original Invoice:</span> ${escapeHtml(returnData.original_bill_number || 'N/A')}
                            </div>
                        </div>

                        <!-- ===== CUSTOMER INFO ===== -->
                        <div class="customer-info">
                            <span class="label">Customer:</span> ${escapeHtml(customerName)}
                            ${customerPhone ? ` | 📞 ${escapeHtml(customerPhone)}` : ''}
                        </div>

                        <!-- ===== RETURN REASON ===== -->
                        ${returnData.return_reason ? `
                        <div class="reason-box">
                            <strong>Reason:</strong> ${escapeHtml(returnData.return_reason)}
                        </div>` : ''}

                        <!-- ===== ITEMS TABLE ===== -->
                        <table class="items-table">
                            <thead>
                                <tr>
                                    <th class="col-item">Item</th>
                                    <th class="col-qty">Qty</th>
                                    <th class="col-price">Price</th>
                                    <th class="col-total">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${returnData.items.map((item) => {
                                    const unitLabel = getUnitLabel(item);
                                    const qtyDisplay = parseFloat(item.quantity).toFixed(0);
                                    const priceDisplay = parseFloat(item.price).toFixed(2);
                                    const totalDisplay = parseFloat(item.total || item.price * item.quantity).toFixed(2);
                                    const itemName = escapeHtml(item.product_name || 'Product');
                                    
                                    return `
                                    <tr>
                                        <td class="col-item">
                                            <span class="item-name">${itemName}</span>
                                        </td>
                                        <td class="col-qty">
                                            <span class="qty-with-unit">${qtyDisplay} <span class="unit">${escapeHtml(unitLabel)}</span></span>
                                        </td>
                                        <td class="col-price">${priceDisplay}</td>
                                        <td class="col-total">${totalDisplay}</td>
                                    </tr>
                                `}).join('')}
                            </tbody>
                        </table>

                        <!-- ===== TOTALS ===== -->
                        <div class="totals">
                            <div class="total-row">
                                <span class="label">Subtotal</span>
                                <span class="value">${formatCurrency(returnData.subtotal)}</span>
                            </div>
                            ${parseFloat(returnData.tax) > 0 ? `
                            <div class="total-row">
                                <span class="label">Tax</span>
                                <span class="value">${formatCurrency(returnData.tax)}</span>
                            </div>` : ''}
                            ${parseFloat(returnData.discount) > 0 ? `
                            <div class="total-row">
                                <span class="label">Discount</span>
                                <span class="value text-danger">-${formatCurrency(returnData.discount)}</span>
                            </div>` : ''}
                            <div class="total-row grand-total">
                                <span class="label">TOTAL</span>
                                <span class="value">${formatCurrency(returnData.grand_total)}</span>
                            </div>
                            <div class="total-row" style="border-top:1px dashed #e0e0e8; padding-top:2px; margin-top:2px;">
                                <span class="label" style="color:#dc2626; font-weight:700;">REFUND</span>
                                <span class="value text-refund">${formatCurrency(returnData.refund_amount)}</span>
                            </div>
                        </div>

                        <!-- ===== RETURN STATUS ===== -->
                        <div class="return-status">● RETURN COMPLETED</div>

                        <!-- ===== PAYMENT METHOD ===== -->
                        ${returnData.payment_method ? `
                        <div class="divider-light"></div>
                        <div class="payment-method">
                            <span class="label">Refund Method</span>
                            <span class="value">${escapeHtml(returnData.payment_method)}</span>
                        </div>
                        ` : ''}

                        <!-- ===== FOOTER ===== -->
                        <div class="footer">
                            <div class="thankyou">✦ Thank You! For Visiting ✦</div>
                            <div class="powered">${translations?.poweredBy || 'Powered by OurZest (PVT) LTD'}</div>
                        </div>
                    </div>

                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                window.onafterprint = function() { 
                                    window.close(); 
                                };
                            }, 400);
                        };
                    </script>
                </body>
                </html>
            `);

            printWindow.document.close();
            printWindow.focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, return is already being processed...');
            return;
        }
        
        isSubmittingRef.current = true;
        if (submitButtonRef.current) {
            submitButtonRef.current.disabled = true;
        }
        
        if (!currentShop?.shop_id) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            isSubmittingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }
        
        if (!validateForm()) {
            isSubmittingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");

            const returnItems = formData.items.filter(item => parseFloat(item.return_quantity) > 0).map(item => ({
                sell_item_id: item.sell_item_id || item.id,
                product_id: item.product_id,
                product_name: item.product_name,
                product_code: item.product_code || '',
                batch_no: item.batch_no || '',
                barcode: item.barcode || '',
                unit: item.unit,
                unit_of_measure: item.unit_of_measure || 'piece',
                packing_type: item.packing_type || null,
                sub_packing_type: item.sub_packing_type || null,
                items_per_pack: item.items_per_pack || 1,
                sell_method: item.sellMethod || 'sub_packing',
                quantity: parseFloat(item.return_quantity),
                price: parseFloat(item.price),
                total: parseFloat(item.price) * parseFloat(item.return_quantity)
            }));

            const payload = {
                action: 'add',
                username: currentUsername,
                shop_id: currentShop.shop_id,
                sell_id: formData.sell_id,
                date: formData.date,
                customer_id: formData.customer_id,
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                subtotal: parseFloat(formData.subtotal),
                tax: parseFloat(formData.tax),
                discount: parseFloat(formData.discount),
                grand_total: parseFloat(formData.grand_total),
                refund_amount: parseFloat(formData.refund_amount),
                payment_method: formData.payment_method,
                return_reason: formData.return_reason,
                notes: formData.notes,
                items: returnItems
            };

            const response = await fetch(RETURN_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations?.returnProcessed || 'Return processed successfully!');
                
                const returnForReceipt = {
                    ...data.data,
                    items: returnItems,
                    subtotal: formData.subtotal,
                    tax: formData.tax,
                    discount: formData.discount,
                    grand_total: formData.grand_total,
                    refund_amount: formData.refund_amount,
                    payment_method: formData.payment_method,
                    return_reason: formData.return_reason,
                    notes: formData.notes,
                    customer_name: formData.customer_name,
                    customer_phone: formData.customer_phone,
                    username: currentUsername,
                    original_bill_number: originalSale?.bill_number,
                    date: formData.date
                };

                clearDraftFromStorage();
                
                setFormData({
                    ...initialFormData,
                    date: new Date().toISOString().split('T')[0],
                    return_number: data.data?.return_number || formData.return_number,
                    return_number_int: data.data?.return_number_int || formData.return_number_int
                });
                setSearchTerm('');
                setInvoiceFound(false);
                setOriginalSale(null);
                setErrors({});
                getNextReturnNumber();
                
                // Only print the receipt - NO MODAL
                setTimeout(() => {
                    handlePrintReceipt(returnForReceipt);
                }, 500);
            } else {
                showToast('error', data.message || translations?.failedProcess || 'Failed to process return');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSubmittingRef.current = false;
            setSaving(false);
            setTimeout(() => { 
                if (submitButtonRef.current) submitButtonRef.current.disabled = false; 
            }, 2000);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const hasItemsToReturn = formData.items.some(item => parseFloat(item.return_quantity) > 0);

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{translations?.processReturn || 'Process Return'}</h1>
                            <p>{translations?.returnItems || 'Search invoice and return items'}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button 
                                className="ozui-action-btn clear-draft" 
                                onClick={clearDraft}
                                disabled={saving}
                                title={translations?.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>
                            
                            <button 
                                className="ozui-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={saving}
                                title={translations?.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>
                            
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    className="ozui-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations?.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.return_number || `Return #${draft.id}`}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.customer_name && <span>{draft.customer_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                {/* Search Invoice Section */}
                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                ref={invoiceSearchRef}
                                type="text"
                                id="invoice_search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchInvoice()}
                                placeholder=" "
                                disabled={saving}
                                autoComplete="off"
                            />
                            <label htmlFor="invoice_search">
                                <FontAwesomeIcon icon={faSearch} />
                                {translations?.searchPlaceholder || 'Enter Invoice Number, Customer Name or Phone'}
                            </label>
                            <button 
                                type="button" 
                                className="ozui-search-btn" 
                                onClick={searchInvoice} 
                                disabled={searching || saving}
                                style={{ position: 'absolute', right: '0.7rem', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--ozui-primary)', cursor: 'pointer' }}
                            >
                                {searching ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                            </button>
                        </div>
                        {errors.invoice && <small className="ozui-error-text">{errors.invoice}</small>}
                    </div>
                </div>

                {invoiceFound && originalSale && (
                    <>
                        {/* Return Information */}
                        <div className="ozui-form-row">
                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        id="return_number"
                                        value={formData.return_number}
                                        readOnly
                                        placeholder=" "
                                        className="ozui-readonly-input"
                                    />
                                    <label htmlFor="return_number">
                                        <FontAwesomeIcon icon={faFileInvoice} />
                                        {translations?.returnNumber || 'Return Number'}
                                    </label>
                                </div>
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="date"
                                        name="date"
                                        id="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                    />
                                    <label htmlFor="date">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        {translations?.returnDate || 'Return Date'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Original Invoice */}
                        <div className="ozui-form-row">
                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        value={originalSale.bill_number}
                                        readOnly
                                        placeholder=" "
                                        className="ozui-readonly-input"
                                    />
                                    <label htmlFor="original_bill">
                                        <FontAwesomeIcon icon={faFileInvoice} />
                                        {translations?.originalBillNumber || 'Original Bill Number'}
                                    </label>
                                </div>
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        value={originalSale.date}
                                        readOnly
                                        placeholder=" "
                                        className="ozui-readonly-input"
                                    />
                                    <label htmlFor="original_date">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        {translations?.originalDate || 'Original Date'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Customer Details */}
                        <div className="ozui-form-row">
                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        value={formData.customer_name}
                                        readOnly
                                        placeholder=" "
                                        className="ozui-readonly-input"
                                    />
                                    <label htmlFor="customer_name">
                                        <FontAwesomeIcon icon={faUser} />
                                        {translations?.customerName || 'Customer Name'}
                                    </label>
                                </div>
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        value={formData.customer_phone}
                                        readOnly
                                        placeholder=" "
                                        className="ozui-readonly-input"
                                    />
                                    <label htmlFor="customer_phone">
                                        <FontAwesomeIcon icon={faPhone} />
                                        {translations?.customerPhone || 'Phone Number'}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        {formData.items.length > 0 && (
                            <ReturnItemsTable 
                                items={formData.items} 
                                onQuantityChange={handleQuantityChange} 
                                onRemove={removeItem} 
                                onToggleSellMethod={toggleSellMethod}
                                errors={errors} 
                                disabled={saving} 
                                translations={translations} 
                                isRTL={isRTL} 
                            />
                        )}
                        {errors.items && <div className="ozui-error-message"><small className="ozui-error-text">{errors.items}</small></div>}

                        {/* Calculations Section */}
                        {hasItemsToReturn && (
                            <>
                                <div className="ozui-form-section">
                                    <div className="ozui-calculations">
                                        <div className="ozui-calculation-row">
                                            <span className="ozui-calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                            <span className="ozui-calculation-value">Rs. {formData.subtotal}</span>
                                        </div>
                                        <div className="ozui-calculation-row">
                                            <span className="ozui-calculation-label">{translations?.tax || 'Tax'}:</span>
                                            <div className="ozui-input-wrapper ozui-small-input">
                                                <input type="number" name="tax" value={formData.tax} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                            </div>
                                        </div>
                                        <div className="ozui-calculation-row">
                                            <span className="ozui-calculation-label">{translations?.discount || 'Discount'}:</span>
                                            <div className="ozui-input-wrapper ozui-small-input">
                                                <input type="number" name="discount" value={formData.discount} onChange={handleChange} step="0.01" min="0" placeholder="0.00" disabled={saving} />
                                            </div>
                                        </div>
                                        <div className="ozui-calculation-row ozui-grand-total">
                                            <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                            <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                                        </div>
                                        <div className="ozui-calculation-row">
                                            <span className="ozui-calculation-label">{translations?.refundMethod || 'Refund Method'}:</span>
                                            <div className="ozui-input-wrapper ozui-small-input">
                                                <select name="payment_method" value={formData.payment_method} onChange={handleChange} disabled={saving}>
                                                    <option value="cash">{translations?.cash || 'Cash'}</option>
                                                    <option value="card">{translations?.card || 'Card'}</option>
                                                    <option value="upi">{translations?.upi || 'UPI'}</option>
                                                    <option value="bank_transfer">{translations?.bankTransfer || 'Bank Transfer'}</option>
                                                    <option value="wallet">{translations?.wallet || 'Wallet'}</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="ozui-calculation-row ozui-refund-amount">
                                            <span className="ozui-calculation-label">{translations?.refundAmount || 'Refund Amount'}:</span>
                                            <span className="ozui-calculation-value" style={{ color: '#dc2626' }}>Rs. {formData.refund_amount}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="ozui-form-actions">
                                    <button 
                                        ref={submitButtonRef}
                                        type="submit" 
                                        className="ozui-btn ozui-btn-primary" 
                                        disabled={saving}
                                        style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}
                                    >
                                        {saving ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                <span>{translations?.saving || 'Processing...'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUndo} />
                                                <span>{translations?.processReturnBtn || 'Process Return'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PharmacyReturn;